<template>
  <div class="task-time-estimate">
    <template v-if="finalEstimate">
      <span class="task-time-estimate__label">Окончательная оценка времени</span>
      <TaskTimeEstimateLi
        :taskId="taskId"
        :isEditTime="userAccess.IsProjectAdmin || isOwner"
        :performer-estimate="finalEstimate"
      />
    </template>
    <template v-else>
      <span class="task-time-estimate__label">Оценка задачи по времени</span>
      <div class="task-time-estimate__list" v-if="performersEstimated.length">
        <TaskTimeEstimateLi
          v-for="performerEstimated in performersEstimated"
          :performer-estimate="performerEstimated"
          :key="performerEstimated.Id"
          :taskId="taskId"
          :isEditTime="isUsersEditDisabled || !canUpsertFinalEstimate || isOwner"
          @removePerformer="removePerformer"
        />
      </div>
      <div class="task-time-estimate__buttons" v-if="mode === TaskTimeEstimateModeEnum.View">
        <TirPmButton
          v-if="showAddButton"
          :class="{ 'task-time-estimate__button-add-grade': !canInvite }"
          :size="TirPmButtonSizeEnum.Small"
          :variant="changeButtonVariant"
          :is-disabled="isDisabled && !isEstimate"
          @click="changeMode(TaskTimeEstimateModeEnum.Add)"
        >
          <template #leftSlot>
            <PlusIcon />
          </template>
          {{ canUpsertFinalEstimate ? 'Выставить окончательную оценку' : 'Добавить оценку' }}
        </TirPmButton>
        <EmployeeMultiselect
          v-if="!isDisabled && allowInvite"
          v-model="invitedEmployees"
          ref="$employeeMultiselect"
          popoverWidth="32.5rem"
          :popoverPosition="{ left: '$el:width - 32.5rem', bottom: '$el:height + 0.25rem' }"
          :employees="employees"
          @open="onOpenMultiselect"
        >
          <template #target>
            <TirPmButton
              :size="TirPmButtonSizeEnum.Small"
              :variant="TirPmButtonVariantEnum.LinkState"
              :is-disabled="isDisabled"
            >
              <template #leftSlot>
                <UserPlusIcon />
              </template>
              Пригласить для оценки
            </TirPmButton>
          </template>
          <template #optionsContentFooter>
            <div class="employee-multiselect_footer">
              <TirPmButton
                :size="TirPmButtonSizeEnum.Small"
                :variant="TirPmButtonVariantEnum.PrimaryState"
                :is-disabled="inviteButtonDisabled"
                :is-loading="isInviteLoading"
                @click="inviteEstimate"
              >
                Пригласить для оценки
              </TirPmButton>
              <TirPmButton
                :size="TirPmButtonSizeEnum.Small"
                :variant="TirPmButtonVariantEnum.SecondaryState"
                @click="closeEmployeeMultiselect"
              >
                Отмена
              </TirPmButton>
            </div>
          </template>
        </EmployeeMultiselect>
      </div>
      <TaskTimeEstimateInput
        v-else
        class="task-time-estimate__input"
        :isDisabled="isDisabled"
        :taskId="taskId"
        :canUpsertFinalEstimate="canUpsertFinalEstimate"
        @upsert="setCurrentUserEstimate"
        @cancel="changeMode(TaskTimeEstimateModeEnum.View)"
      />
    </template>
  </div>
</template>
<script lang="ts" setup>
import { computed, PropType, ref, watch } from 'vue';
import { PerformerEstimatedModel } from '../../models';
import TaskTimeEstimateLi from './task-time-estimate-li.vue';
import TaskTimeEstimateInput from './task-time-estimate-input.vue';
import { TirPmButton, TirPmButtonSizeEnum, TirPmButtonVariantEnum } from 'tir-components';
import { PlusIcon, UserPlusIcon } from 'tir-style-system/icons/outline';
import { TaskTimeEstimateModeEnum } from './enums';
import { CurrentUser, UserRoleEnum } from 'tir-pm-user-core';
import { EmployeeMultiselect } from '@/components';
import { AdapterService } from './adapter-service';
import { UserModel } from '@/components/task-forms/models';
import GetAccessCurrentUserInQueueTaskByIdResponseModel from '../task-drawer/models/GetAccessCurrentUserInQueueTaskByIdResponseModel';

/**API сервис */
const apiService = new AdapterService();

/**Параметры */
const props = defineProps({
  /**Разрешение на приглашение для оценки задачи */
  canInvite: { type: Boolean as PropType<boolean>, default: true },
  /**Список оценок по исполнителям */
  performersEstimated: { type: Array as PropType<Array<PerformerEstimatedModel>>, default: [] },
  /**Заблокировать */
  isDisabled: { type: Boolean, default: false },
  /**Id задачи */
  taskId: { type: Number },
  /**Может ли текущий пользователь выставлять итоговую оценку времени */
  canUpsertFinalEstimate: { type: Boolean },
  /**Финальная оценка */
  finalEstimatePerformer: { type: Object as PropType<PerformerEstimatedModel> },
  /**Оценка задачи */
  isEstimate: { type: Boolean },
  /**Доступы юзера к очереди */
  userAccess: { type: Object as PropType<GetAccessCurrentUserInQueueTaskByIdResponseModel> },
});

/**Режим */
const mode = ref(TaskTimeEstimateModeEnum.View);
/**Компонент выбора пользователей */
const $employeeMultiselect = ref<InstanceType<typeof EmployeeMultiselect>>();
/**Список сотрудников */
const employees = ref<UserModel[]>([]);
/**Приглашенные сотрудники */
const invitedEmployees = ref<number[]>([]);
/**Приглашение пользователей */
const isInviteLoading = ref(false);
/**Финальная оценка */
const finalEstimate = ref<PerformerEstimatedModel>();

/**Сменить режимм */
const changeMode = (_mode: TaskTimeEstimateModeEnum) => {
  mode.value = _mode;
};

/**Пользоватер овнер */
const isOwner = computed(() => CurrentUser.Roles.includes(UserRoleEnum.Owner));
/**Разрешено приглашение на оценку */
const allowInvite = computed(() => {
  return (
    !props.isEstimate &&
    (isOwner.value || props.userAccess.IsProjectAdmin || props.userAccess.IsNamespaceAdmin)
  );
});
/**Вариант кнопки */
const changeButtonVariant = computed(() => {
  if (props.canInvite) {
    return TirPmButtonVariantEnum.SecondaryState;
  } else {
    return TirPmButtonVariantEnum.PrimaryState;
  }
});
/**Показывать кнопку добавления оценки */
const showAddButton = computed(() => {
  return !props.performersEstimated.some((x) => x.Id == CurrentUser.Id && x.Estimate);
});
/**Отключена кнопка приглашения */
const inviteButtonDisabled = computed(() => {
  const alreadyInvited = props.performersEstimated?.map((x) => x.Id) || [];
  return (
    alreadyInvited.length == invitedEmployees.value.length &&
    alreadyInvited.every((x) => invitedEmployees.value.includes(x))
  );
});
/**Запрещено редактирование пользователей */
const isUsersEditDisabled = computed(() => props.performersEstimated.some((p) => p.IsEdit));

/**Отслеживание изменений финальной оценки */
watch(() => props.finalEstimatePerformer, setFinalEstimate, { deep: true, immediate: true });

/**Установка значения поля finalEstimate */
function setFinalEstimate() {
  console.log(props.finalEstimatePerformer);

  finalEstimate.value = props.finalEstimatePerformer;
}
/**Пригласить пользователей к оценке */
const inviteEstimate = async () => {
  isInviteLoading.value = true;

  const response = await apiService.inviteToEstimate(props.taskId, invitedEmployees.value);
  if (response.IsSuccess) {
    invitedEmployees.value.forEach((id) => {
      if (!props.performersEstimated.some((p) => p.Id == id)) {
        const employee = employees.value.find((e) => e.Id == id);
        if (employee) {
          props.performersEstimated.push(
            new PerformerEstimatedModel({
              Id: employee.Id,
              FirstName: employee.FirstName,
              LastName: employee.LastName,
              MiddleName: employee.MiddleName,
              AvatarColor: employee.AvatarColor,
              Foto: employee.ImageUrl,
            }),
          );
        }
      }
    });
  }

  isInviteLoading.value = false;
  closeEmployeeMultiselect();
};
/**Закрыть поповер выбора пользователей */
const closeEmployeeMultiselect = () => {
  $employeeMultiselect.value?.close?.();
};
/**Получение пользователей для приглашения */
const onOpenMultiselect = async () => {
  invitedEmployees.value = props.performersEstimated?.map((x) => x.Id) || [];
  const response = await apiService.getEmployeesForManagerList();
  if (response.IsSuccess)
    employees.value = response.Value.filter(
      (e) => !props.performersEstimated.some((p) => p.Id == e.Id),
    );
};
/**Удаление сотрудника из массива */
const removePerformer = (performerId: number) => {
  const performerIndex = props.performersEstimated.findIndex((p) => p.Id == performerId);
  if (performerIndex != -1) props.performersEstimated.splice(performerIndex, 1);
};
/**Установка оценки текущим пользователем */
const setCurrentUserEstimate = (estimate: number) => {
  const userEstimate = new PerformerEstimatedModel({
    Id: CurrentUser.Id,
    FirstName: CurrentUser.FirstName,
    LastName: CurrentUser.LastName,
    MiddleName: CurrentUser.MiddleName,
    AvatarColor: CurrentUser.AvatarColor,
    Foto: CurrentUser.Foto,
    Estimate: estimate,
    DateUpsert: new Date(),
  });

  props.performersEstimated.push(userEstimate);
  if (props.canUpsertFinalEstimate) {
    finalEstimate.value = userEstimate;
  }

  changeMode(TaskTimeEstimateModeEnum.View);
};
</script>
<style lang="scss" scoped>
.task-time-estimate {
  &__label {
    display: block;
    margin-bottom: 0.25rem;
    color: var(--text-secondary, #686c73);
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1rem;
  }

  &__buttons {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-top: 0.25rem;

    .button {
      max-width: fit-content;
    }
  }

  &__input {
    margin-top: 0.25rem;
  }

  &__button-add-grade {
    width: 15.313rem !important;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
}
</style>
<style lang="scss">
.employee-multiselect_footer {
  display: flex;
  align-items: center;
  padding: 0.75rem 0.75rem 0.25rem;
  gap: 0.75rem;
}
</style>
